import React from 'react';

import Form from 'mobx-react-form';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { Box, InputAdornment, TableCell, TableRow, Tooltip } from '@mui/material';
import { toNumber, isNumber } from 'lodash';

import { getInputStyles } from './IncomeAdjustmentRow';
import NumericInput from 'components/shared/inputs/NumericInput';
import { LossItem } from 'types/underwriting/income-approach/cap-rate-conclusion';
import { LOSS_ITEM_KEYS } from '../constants';
import { formatCurrencyFloat, toPercentageString } from 'shared/utils/numberFormatters';
import { divide } from 'shared/utils/numberOperations';

type LossItemTableRowProps = {
  form: Form;
  fieldPath: string;
  lossItem: LossItem;
  entrepreneurialProfitTotal: number;
};

const LossItemTableRow = ({ form, fieldPath, lossItem, entrepreneurialProfitTotal }: LossItemTableRowProps) => {
  const amountPrefix = lossItem.amount !== 0 ? '-$' : '$';
  const isValueOnly = [
    LOSS_ITEM_KEYS.ENTREPRENEURIAL_PROFIT,
    LOSS_ITEM_KEYS.BUYOUT_COST,
    LOSS_ITEM_KEYS.COMMISSION_FEE,
  ].includes(lossItem.name);
  const isEntrepreneurialProfit = lossItem.name === LOSS_ITEM_KEYS.ENTREPRENEURIAL_PROFIT;

  const getTooltipTitle = (lossItem: LossItem) => {
    const percentValue = divide(lossItem.amount || 0, 100);
    const dollarValue = percentValue * entrepreneurialProfitTotal;

    const entrepreneurialProfitPercentValue = toPercentageString(percentValue, 2);
    const totalDollarValue = formatCurrencyFloat(entrepreneurialProfitTotal);
    const entrepreneurialProfitDollarValue = formatCurrencyFloat(dollarValue);

    return (
      `Entrepreneurial profit is equal to ${entrepreneurialProfitPercentValue} ` +
      `of the sum of all of the line items above, ${totalDollarValue}, which totals ` +
      `to ${entrepreneurialProfitDollarValue}`
    );
  };

  const handleChange = (value: number) => {
    if (!isNumber(value)) {
      return;
    }

    try {
      const targetField = form.$(fieldPath);

      const dataToSet = isValueOnly
        ? { amount: value }
        : { months: value, amount: value * (targetField.value.monthlyLoss || 0) };

      targetField.set({
        ...targetField.value,
        ...dataToSet,
      });
    } catch (error) {
      console.error('Error setting field', error);
    }
  };

  return (
    <TableRow>
      <TableCell>Less {lossItem.name}</TableCell>
      <TableCell align="right">
        {!isValueOnly && (
          <NumericInput
            sx={{ ...getInputStyles(true) }}
            value={lossItem.months}
            onFocus={(event) => event.target?.select()}
            onChange={(value) => handleChange(toNumber(value))}
            allowNegative={false}
            fullWidth={false}
            placeholder="0"
            margin="none"
            thousandSeparator
            InputProps={{
              endAdornment: <InputAdornment position="end">months</InputAdornment>,
            }}
          />
        )}
      </TableCell>
      <TableCell align="right" sx={{ position: 'relative' }}>
        <NumericInput
          sx={{ ...getInputStyles(true) }}
          value={lossItem.amount}
          onFocus={(event) => event.target?.select()}
          onChange={isValueOnly ? (value) => handleChange(toNumber(value)) : undefined}
          allowNegative={false}
          disabled={!isValueOnly && !isEntrepreneurialProfit}
          autoComplete="off"
          decimalScale={2}
          fullWidth={false}
          margin="none"
          placeholder="0.00"
          thousandSeparator
          {...(isEntrepreneurialProfit
            ? {
                InputProps: {
                  sx: { paddingRight: 0 },
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                },
              }
            : { prefix: amountPrefix })}
        />
        {isEntrepreneurialProfit && (
          <Box
            sx={{
              position: 'absolute',
              right: -16,
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
            }}
          >
            <Tooltip title={getTooltipTitle(lossItem)} placement="top">
              <InfoOutlined fontSize="small" />
            </Tooltip>
          </Box>
        )}
      </TableCell>

      <TableCell />
      <TableCell />
    </TableRow>
  );
};

export default LossItemTableRow;
